import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/store';
import { authenticationLogout, logout } from '@/store/slices/authentication';
import { userBrandsList } from '@/store/slices/casino-instance';
import { OWNERS_HUB_ROUTES } from '@/constants/routes';
import { useLanguageSwitcher, languageName } from '@/nova/hooks/useLanguageSwitcher';

import { useConfigurator } from '../../../store/configurator';
import { UserGlyph, HomeGlyph, ChevronGlyph, CheckGlyph } from './glyphs';
import {
  Root,
  HomeButton,
  Trigger,
  Popup,
  SectionLabel,
  SectionLabelText,
  Divider,
  Field,
  FieldLabel,
  FieldLabelText,
  ReadonlyBox,
  EmailText,
  SelectBox,
  ValueText,
  Chevron,
  OptionList,
  Option,
  OptionText,
  Check,
  LogoutButton,
  LogoutText,
} from './UserMenu.styles';

/** Default language when nothing is stored (per spec: English ISO code). */
const DEFAULT_LANGUAGE = 'en';

/** Which select dropdown (if any) is expanded. */
type OpenSelect = 'language' | 'currency' | null;

/**
 * Account icon + popover, shown on every desktop step of the wizard (mobile is
 * handled separately, later). Pinned to the top-right of the preview pane; the
 * popover opens beneath it, right-aligned to the icon.
 *
 * Data:
 *  - email     — the authenticated session email (from the configurator store).
 *  - language  — the site's configured set (`window.config.languages`) via the shared
 *                `useLanguageSwitcher` hook (the config-driven language-switcher contract).
 *                Selecting persists + re-translates and re-localises the native shell.
 *  - logout    — POSTs to the auth-service logout endpoint, then clears the local
 *                session and redirects to /login.
 */
export const UserMenu = (): React.ReactElement => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { emailAddress, currency, currencies, setCurrency } = useConfigurator();

  // Show a "back to Owner Studio" Home shortcut ONLY for owners who already have
  // other brands — a first-time buyer building their first brand has no studio to
  // return to yet. Same ownership signal PaymentSuccess uses; self-fetch the list
  // once if we arrived without it (e.g. a cold magic-link entry) so the shortcut is
  // reliable however the wizard was reached. The fetch only populates the list (no
  // active-brand / navigation side effect) and is a no-op once loaded/in-flight.
  const hasBrands = useAppSelector((s) => s.casinoInstance.brands.length > 0);
  const brandsLoaded = useAppSelector((s) => s.casinoInstance.loaded);
  const brandsLoading = useAppSelector((s) => s.casinoInstance.loading);
  const requestedBrands = React.useRef(false);
  React.useEffect(() => {
    if (requestedBrands.current) return;
    requestedBrands.current = true;
    if (!brandsLoaded && !brandsLoading) dispatch(userBrandsList());
  }, [dispatch, brandsLoaded, brandsLoading]);
  // The offered languages + active code + the change contract all come from the shared
  // `useLanguageSwitcher` hook (the SAME wiring the config-driven `module.languageSwitcher`
  // and the `/login` + `/register` screens use). The list is `window.config.languages` (the
  // site's full configured set (matching Settings and the account picker), not narrowed to a
  // selected market and NOT the `/configurator` catalog; selecting dispatches `changeLanguage`
  // + `changeAccountLanguage` and emits the native `CHANGE_LANGUAGE` event.
  const { languages, currentLanguage, onChangeLanguage } = useLanguageSwitcher();

  // `currentLanguage` is already normalised to the base ISO code ("en-US" → "en"); fall back
  // to English when nothing is set so a row still shows selected.
  const language = currentLanguage || DEFAULT_LANGUAGE;
  // Build the options from the configured codes, each labelled via the curated map. Falls back
  // to the active language alone when `window.config.languages` is unset.
  const languageOptions =
    languages.length > 0
      ? languages.map((code) => ({ code, name: languageName(code) }))
      : [{ code: language, name: languageName(language) }];
  // Name shown on the collapsed field: the active language's display name.
  const currentLanguageName = languageName(language);

  const [open, setOpen] = React.useState(false);
  const [openSelect, setOpenSelect] = React.useState<OpenSelect>(null);
  const rootRef = React.useRef<HTMLDivElement>(null);

  const close = React.useCallback((): void => {
    setOpen(false);
    setOpenSelect(null);
  }, []);

  // Close on outside click + Escape while the popover is open. A click on inert
  // space INSIDE the popover (outside any select region) collapses an open
  // dropdown, so the selects behave like a real <select>.
  React.useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e: MouseEvent): void => {
      const target = e.target as Element | null;
      if (!rootRef.current || !target) return;
      if (!rootRef.current.contains(target)) {
        close();
        return;
      }
      if (openSelect && !target.closest('[data-cfg-select]')) setOpenSelect(null);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, openSelect, close]);

  const toggleSelect = (which: Exclude<OpenSelect, null>): void => {
    setOpenSelect((prev) => (prev === which ? null : which));
  };

  const selectLanguage = (code: string): void => {
    // The hook runs the full language-switcher contract: changeLanguage (persists +
    // re-translates via i18n) + changeAccountLanguage + the native CHANGE_LANGUAGE event.
    onChangeLanguage(code);
    setOpenSelect(null);
  };

  const selectCurrency = (code: string): void => {
    // Re-prices the whole session server-side via `/set-currency` (the bound campaigns +
    // configuration_data.currency) and refetches the recommended packs. No-ops when unchanged.
    setCurrency(code);
    setOpenSelect(null);
  };

  const onLogout = async (): Promise<void> => {
    // Call the auth-service logout endpoint; it resolves regardless of outcome.
    await dispatch(authenticationLogout());
    // Always clear the local session + leave the authenticated shell.
    dispatch(logout());
    navigate('/login');
  };

  return (
    <Root ref={rootRef}>
      {hasBrands ? (
        <HomeButton
          to={OWNERS_HUB_ROUTES.STUDIO_HOME}
          data-ga-id="cfg-wizard-home"
          aria-label={t('Home')}
          title={t('Home')}
        >
          <HomeGlyph />
        </HomeButton>
      ) : null}
      <Trigger
        type="button"
        data-ga-id="cfg-wizard-user-menu-toggle"
        $open={open}
        onClick={() => (open ? close() : setOpen(true))}
        aria-label={t('Account')}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <UserGlyph />
      </Trigger>

      {open ? (
        <Popup role="dialog" aria-label={t('Account details')}>
          <SectionLabel>
            <SectionLabelText text="ACCOUNT DETAILS" />
          </SectionLabel>
          <Divider />

          <Field>
            <FieldLabel>
              <FieldLabelText text="Connected as" />
            </FieldLabel>
            <ReadonlyBox>
              <EmailText text={emailAddress || '–'} />
            </ReadonlyBox>
          </Field>

          <Field>
            <FieldLabel>
              <FieldLabelText text="Language" />
            </FieldLabel>
            <SelectBox
              type="button"
              data-ga-id="cfg-wizard-language-toggle"
              data-cfg-select
              $open={openSelect === 'language'}
              onClick={() => toggleSelect('language')}
              aria-haspopup="listbox"
              aria-expanded={openSelect === 'language'}
            >
              <ValueText text={currentLanguageName} raw />
              <Chevron $open={openSelect === 'language'}>
                <ChevronGlyph />
              </Chevron>
            </SelectBox>
            {openSelect === 'language' ? (
              <OptionList role="listbox" data-cfg-select>
                {languageOptions.map((opt) => (
                  <Option
                    key={opt.code}
                    type="button"
                    data-ga-id={`cfg-wizard-language-${opt.code}`}
                    role="option"
                    aria-selected={opt.code === language}
                    $selected={opt.code === language}
                    onClick={() => selectLanguage(opt.code)}
                  >
                    <OptionText text={opt.name} $selected={opt.code === language} raw />
                    {opt.code === language ? (
                      <Check>
                        <CheckGlyph />
                      </Check>
                    ) : null}
                  </Option>
                ))}
              </OptionList>
            ) : null}
          </Field>

          {/* Currency selector: priced server-side via `/set-currency`, so switching re-prices the
              whole session. Shown only when the catalogue offers more than one active currency. */}
          {currencies.length > 1 ? (
            <Field>
              <FieldLabel>
                <FieldLabelText text="Currency" />
              </FieldLabel>
              <SelectBox
                type="button"
                data-ga-id="cfg-wizard-currency-toggle"
                data-cfg-select
                $open={openSelect === 'currency'}
                onClick={() => toggleSelect('currency')}
                aria-haspopup="listbox"
                aria-expanded={openSelect === 'currency'}
              >
                <ValueText text={currency} raw />
                <Chevron $open={openSelect === 'currency'}>
                  <ChevronGlyph />
                </Chevron>
              </SelectBox>
              {openSelect === 'currency' ? (
                <OptionList role="listbox" data-cfg-select>
                  {currencies.map((code) => (
                    <Option
                      key={code}
                      type="button"
                      data-ga-id={`cfg-wizard-currency-${code}`}
                      role="option"
                      aria-selected={code === currency}
                      $selected={code === currency}
                      onClick={() => selectCurrency(code)}
                    >
                      <OptionText text={code} $selected={code === currency} raw />
                      {code === currency ? (
                        <Check>
                          <CheckGlyph />
                        </Check>
                      ) : null}
                    </Option>
                  ))}
                </OptionList>
              ) : null}
            </Field>
          ) : null}

          <Divider $spaced />

          <LogoutButton type="button" data-ga-id="cfg-wizard-user-menu-logout" onClick={onLogout}>
            <LogoutText text="Logout" />
          </LogoutButton>
        </Popup>
      ) : null}
    </Root>
  );
};

export default UserMenu;
